const { BaseDAO } = require('./BaseDAO.js');

class AuthorDAO extends BaseDAO {
  constructor() {
    super();
    this.authorDbPath = '/blog/author.db.json';
    this.cacheTTL = 15 * 60 * 1000; // 15 minutes for author data (changes less frequently)
  }

  // Get author database
  async getAuthorDatabase() {
    return this.fetchWithCache(this.authorDbPath, 'author_db', this.cacheTTL);
  }

  // Get single author by slug
  async getAuthor(authorSlug) {
    if (!authorSlug) return null;
    
    const cacheKey = `author_${authorSlug}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const authorDb = await this.getAuthorDatabase();
      const author = authorDb.authors[authorSlug];
      
      if (author) {
        this.setCached(cacheKey, author);
        return author;
      }
      
      return null;
    } catch (error) {
      console.error(`Failed to load author: ${authorSlug}`, error);
      return null;
    }
  }

  // Get multiple authors by slugs
  async getAuthors(authorSlugs) {
    if (!authorSlugs || authorSlugs.length === 0) return [];
    
    const results = [];
    const uncachedSlugs = [];
    
    // Check cache first
    for (const slug of authorSlugs) {
      const cached = this.getCached(`author_${slug}`);
      if (cached) {
        results.push({ slug, author: cached });
      } else {
        uncachedSlugs.push(slug);
      }
    }
    
    // Load uncached authors
    if (uncachedSlugs.length > 0) {
      try {
        const authorDb = await this.getAuthorDatabase();
        
        for (const slug of uncachedSlugs) {
          const author = authorDb.authors[slug];
          if (author) {
            this.setCached(`author_${slug}`, author);
            results.push({ slug, author });
          }
        }
      } catch (error) {
        console.error('Failed to load authors:', uncachedSlugs, error);
      }
    }
    
    return results;
  }

  // Get all authors
  async getAllAuthors() {
    try {
      const authorDb = await this.getAuthorDatabase();
      return Object.entries(authorDb.authors || {}).map(([slug, author]) => ({
        slug,
        ...author
      }));
    } catch (error) {
      console.error('Failed to load all authors:', error);
      return [];
    }
  }

  // Search authors by name or bio
  async searchAuthors(query) {
    if (!query || query.trim().length < 2) return [];
    
    const searchTerm = query.toLowerCase().trim();
    const allAuthors = await this.getAllAuthors();
    
    return allAuthors.filter(author => {
      const name = (author.name || '').toLowerCase();
      const bio = (author.bio || '').toLowerCase();
      const expertise = (author.expertise || []).join(' ').toLowerCase();
      
      return name.includes(searchTerm) || 
             bio.includes(searchTerm) || 
             expertise.includes(searchTerm);
    });
  }

  // Get author stats (story count, etc.)
  async getAuthorStats(authorSlug) {
    const cacheKey = `author_stats_${authorSlug}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const author = await this.getAuthor(authorSlug);
      if (!author) return null;

      const stats = {
        storyCount: author.storyCount || 0,
        totalReadTime: author.totalReadTime || 0,
        categories: author.categories || [],
        expertise: author.expertise || [],
        joinDate: author.joinDate || null,
        featuredStories: author.featuredStories || 0
      };

      this.setCached(cacheKey, stats, this.cacheTTL * 2); // Cache stats longer
      return stats;
    } catch (error) {
      console.error(`Failed to load author stats: ${authorSlug}`, error);
      return null;
    }
  }

  // Get authors by expertise/category
  async getAuthorsByExpertise(expertise) {
    const cacheKey = `authors_expertise_${expertise.toLowerCase()}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const allAuthors = await this.getAllAuthors();
      const matchingAuthors = allAuthors.filter(author => {
        const authorExpertise = author.expertise || [];
        const categories = author.categories || [];
        
        return authorExpertise.some(exp => exp.toLowerCase().includes(expertise.toLowerCase())) ||
               categories.some(cat => cat.toLowerCase().includes(expertise.toLowerCase()));
      });

      this.setCached(cacheKey, matchingAuthors);
      return matchingAuthors;
    } catch (error) {
      console.error(`Failed to load authors by expertise: ${expertise}`, error);
      return [];
    }
  }

  // Get top authors by story count
  async getTopAuthors(limit = 10) {
    const cacheKey = `top_authors_${limit}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const allAuthors = await this.getAllAuthors();
      const topAuthors = allAuthors
        .sort((a, b) => (b.storyCount || 0) - (a.storyCount || 0))
        .slice(0, limit);

      this.setCached(cacheKey, topAuthors);
      return topAuthors;
    } catch (error) {
      console.error('Failed to load top authors:', error);
      return [];
    }
  }

  // Validate author exists
  async authorExists(authorSlug) {
    if (!authorSlug) return false;
    
    try {
      const author = await this.getAuthor(authorSlug);
      return author !== null;
    } catch (error) {
      return false;
    }
  }

  // Get author profile with extended information
  async getAuthorProfile(authorSlug) {
    const cacheKey = `author_profile_${authorSlug}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const author = await this.getAuthor(authorSlug);
      const stats = await this.getAuthorStats(authorSlug);
      
      if (!author) return null;

      const profile = {
        ...author,
        slug: authorSlug,
        stats: stats || {},
        profileImage: author.profileImage || this.getDefaultProfileImage(authorSlug),
        socialLinks: author.socialLinks || {},
        recentActivity: author.recentActivity || null
      };

      this.setCached(cacheKey, profile);
      return profile;
    } catch (error) {
      console.error(`Failed to load author profile: ${authorSlug}`, error);
      return null;
    }
  }

  // Generate default profile image path
  getDefaultProfileImage(authorSlug) {
    return `/blog/authors/${authorSlug}/profile.webp`;
  }
}

module.exports = { AuthorDAO };